import React, { useMemo, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { getAllCustomers } from "actions/user";
import { MSpinner, MSelect } from "components/Shared";
import { MSelectOption } from "components/Shared/MSelect";
import Button from "@mui/material/Button";
import { getSettings } from "requests/customerCalcSettings";
import { 
  CustomerRCTAccountSettingsDto, 
  CustomerRCTCalculationSettingsDto,
  Customer360ValueSettingsDto,
  CustomerDto,
  CustomerCalcSettingsDto,
} from "interfaces/Mueller.Business.Models";
import Customer360ValueSettingsForm from "./Customer360ValueSettingsForm";
import CustomerRCTSettingsCombined from "./CustomerRCTSettingsCombined";
import { CalcToolCodes } from "../../../typeConstants/calcTypeCodes";

interface CustomerCalcSettingsWizardProps {
  customerNumber: number;
  onAdd360Value: (settings: Customer360ValueSettingsDto) => void;
  onAddRCT: (accountSettings: CustomerRCTAccountSettingsDto,  calcSettings: CustomerRCTCalculationSettingsDto) => void;
  onCancel: () => void;
  portalTargetClass: string;
  // Redux
  customerList: CustomerDto[];
  getAllCustomers: () => void;
  fetchingCustomerList: boolean;
}

enum WizardStep {
  Select,
  Calc360Preview,
  RCTPreview,
};

const CustomerCalcSettingsWizard = (props: CustomerCalcSettingsWizardProps) => {
  const [currentStep, setCurrentStep] = useState(WizardStep.Select);
  const [calcType, setCalcType] = useState<MSelectOption>();
  const [copyCustomer, setCopyCustomer] = useState<MSelectOption>();
  const [settings, setSettings] = useState<CustomerCalcSettingsDto>();
  const mounted = useRef(true);

  useEffect(() => {
    if (!props.customerList?.length) props.getAllCustomers();
    return () => {
      mounted.current = false;
    };
  }, []);

  // Customer and Calc Type MSelects

  const customerOptions = props.customerList
    ? props.customerList.map(c => ({ value: c.accountNumber, label: `${c.accountNumber} - ${c.customerName}` }))
    : [];

  const calcTypeOptions = settings?.accountSettings.map(s => ({ value: s.code, label: s.name })) ?? [];

  const onCopyCustomerChange = (option: MSelectOption) => {
    setCopyCustomer(option);
    setCalcType(null);
    const handleSettings = (settings: CustomerCalcSettingsDto) => {
      // Need to update customer number to the target account
      const updatedSettings = global.structuredClone(settings);
      updatedSettings.accountSettings.forEach(s => {
        switch(s.code) {
          case CalcToolCodes.Calc360Value:
            s.settings360Value.customerNumber = props.customerNumber;
            break;
          default:
            s.settingsRCT.customerNumber = props.customerNumber;
        }
      });
      setSettings(updatedSettings);
    }
    getSettings(option.value, handleSettings, mounted);
  };
  const onCalcTypeChange = (option: MSelectOption) => setCalcType(option);

  // Wizard

  const onPrevious = () => {
    switch(currentStep) {
      case WizardStep.Select:
        props.onCancel();
        break;
      case WizardStep.Calc360Preview:
      case WizardStep.RCTPreview:
        setCurrentStep(WizardStep.Select);
        break;
    }
  };

  const onNext = () => {
    switch(currentStep) {
      case WizardStep.Select:
        switch(calcType.value) {
          case CalcToolCodes.Calc360Value:
            setCurrentStep(WizardStep.Calc360Preview);
            break;
          default:
            setCurrentStep(WizardStep.RCTPreview);
            break;
        }
        break;
      default:
        // Save is handled in parent component 
        break;
    }
  };

  const calc360Settings = settings?.accountSettings.find(s => s.code === CalcToolCodes.Calc360Value)?.settings360Value;
  const rctAccountSettings = settings?.accountSettings.find(s => s.code === calcType?.value)?.settingsRCT;

  // Render

  const stepToComponent = {
    [WizardStep.Select]: <div>
                          <MSelect
                            id="selectAccount"
                            placeholder="Select account:"
                            className="md-full-width"
                            options={customerOptions}
                            value={copyCustomer}
                            onChange={onCopyCustomerChange}
                            name="Customer Account"
                            getMenuPortalTarget={() => document.querySelector(props.portalTargetClass)}
                          />
                          {(copyCustomer && !calcTypeOptions.length) && <p>No calc settings for this customer</p>}
                          {(copyCustomer && calcTypeOptions.length > 0) && 
                            <MSelect
                              id="selectCalcType"
                              placeholder="Select calc type:"
                              className="md-full-width"
                              options={calcTypeOptions}
                              value={calcType}
                              onChange={onCalcTypeChange}
                              name="Customer Account"
                              getMenuPortalTarget={() => document.querySelector(props.portalTargetClass)}
                            />
                          }
                          <div className="customer-calc-settings-wizard-button-container">
                            <Button
                              onClick={onPrevious}
                            >Cancel</Button>
                            <Button
                              onClick={onNext}
                              disabled={!copyCustomer || !calcType}
                            >Next</Button>
                          </div>
                        </div>,
    [WizardStep.Calc360Preview]: <Customer360ValueSettingsForm
                                    customerNumber={props.customerNumber}
                                    settings={calc360Settings}
                                    onSave={props.onAdd360Value}
                                    onCancel={onPrevious}
                                    canEdit={true}
                                    portalTargetClass={props.portalTargetClass}
                                  />,
    [WizardStep.RCTPreview]: <CustomerRCTSettingsCombined
                                customerNumber={props.customerNumber}
                                accountSettings={rctAccountSettings}
                                calcSettings={settings?.calculationSettings}
                                onSave={props.onAddRCT}
                                onCancel={onPrevious}
                                canEdit={true}
                                portalTargetClass={props.portalTargetClass}
                              />
  };

  if (props.fetchingCustomerList) return (<MSpinner />);

  return (
    <div className="customer-calc-settings-wizard-container">
      {stepToComponent[currentStep]}
    </div>
  );
};

const mapStateToProps = state => ({
  customerList: state.user.allCustomers,
  fetchingCustomerList: state.user.isFetchingAllCustomersList,

});

const mapDispatchToProps = dispatch => ({
  getAllCustomers() {
    dispatch(getAllCustomers());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomerCalcSettingsWizard);
